import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  QueryFunctionContext,
} from '@tanstack/react-query';
import {
  ChatbotMessage,
  ChatbotSendMessageRequest,
  ChatbotSendMessageResponse,
  ChatbotThreadListResponse,
  ReferenceCard,
  ChatbotReference,
  SuggestionsResponse,
} from '../../models/chatbot/types';
import {
  getChatThreadMessages,
  getChatThreadList,
  sendMessage,
  getReferenceCard,
  getSuggestions,
} from '../../services/chatbot';
import {
  chatMessagesQueryKey,
  transformMessageReferences,
} from '../../lib/chatbot-utils';

/**
 * Query key factory for chat thread list
 */
const chatThreadListQueryKey = (programId: string, perPage: number) => [
  'chat-thread-list',
  programId,
  perPage.toString(),
];

/**
 * Query key factory for reference cards
 */
const referenceCardQueryKey = (referenceType: string, referenceId: string) => [
  'reference-card',
  referenceType,
  referenceId,
];

/**
 * Query key factory for suggestions
 */
const suggestionsQueryKey = (programId: string) => [
  'chatbot-suggestions',
  programId,
];

/**
 * Fetch all messages for a specific chat thread from the API
 *
 * This query retrieves all messages in a conversation thread between
 * the user and the chatbot assistant. Returns an array of messages with
 * full conversation history, timestamps, and message metadata.
 *
 * @param programId - The program ID for multi-tenant isolation
 * @param threadId - The unique identifier of the chat thread to fetch messages for
 * @param options - Optional React Query configuration options
 * @returns Query result containing an array of ChatbotMessage
 */
export const useChatbotThreadMessagesQuery = (
  programId: string,
  threadId?: string,
  options?: Omit<
    UseQueryOptions<ChatbotMessage[], Error, ChatbotMessage[], string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<ChatbotMessage[], Error, ChatbotMessage[], string[]>({
    queryKey: chatMessagesQueryKey(programId, threadId),
    queryFn: async (): Promise<ChatbotMessage[]> => {
      if (!threadId) {
        throw new Error('Thread ID is required');
      }
      const response = await getChatThreadMessages(threadId);
      // Ensure all messages from API have isStreaming: false
      // and transform serialized cards in references to flat format
      return response.data.data.map((message) =>
        transformMessageReferences({
          ...message,
          isStreaming: false,
        })
      );
    },
    ...options,
    // Ensure enabled from options takes precedence, but default to our check
    enabled: options?.enabled ?? (!!threadId && !!programId),
  });

  return query;
};

/**
 * Fetch infinite chat thread list from the API
 *
 * This infinite query retrieves a paginated list of all chat threads for the user,
 * ordered by most recent activity. Each thread contains metadata but not
 * the full message history (use useChatbotThreadMessagesQuery for full messages).
 *
 * The query supports infinite scrolling by automatically loading more pages
 * as needed. Each page contains a list of threads with pagination metadata.
 *
 * @param programId - The program ID for multi-tenant isolation
 * @param perPage - Number of threads per page (default: 20)
 * @param options - Optional React Query infinite query configuration options
 * @returns Infinite query result with pages of thread lists and pagination metadata
 */
export const useChatbotThreadListQuery = (
  programId: string,
  perPage = 20,
  options?: Omit<
    UseInfiniteQueryOptions<
      ChatbotThreadListResponse,
      Error,
      ChatbotThreadListResponse,
      ChatbotThreadListResponse,
      string[]
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const query = useInfiniteQuery<
    ChatbotThreadListResponse,
    Error,
    ChatbotThreadListResponse,
    string[]
  >({
    queryKey: chatThreadListQueryKey(programId, perPage),
    queryFn: async ({
      pageParam,
    }: QueryFunctionContext<string[], number | null>) => {
      const page = pageParam ?? 1;
      const response = await getChatThreadList(page, perPage);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.page < lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined;
    },
    enabled: !!programId,
    ...options,
  });

  return query;
};

/**
 * Send a message to a chat thread mutation
 *
 * This mutation sends a new message to an existing thread or creates a new
 * thread if no threadId is provided. The mutation automatically updates the
 * thread cache with the new message and thread data upon successful completion.
 *
 * The mutation handles both user messages and assistant responses, updating
 * the local cache to reflect the new conversation state immediately.
 *
 * @param programId - The program ID for multi-tenant isolation
 * @returns Mutation object for sending messages with automatic cache updates
 */
export const useChatbotSendMessageMutation = (programId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    ChatbotSendMessageResponse,
    Error,
    ChatbotSendMessageRequest
  >({
    mutationFn: async ({ threadId, message }) => {
      const response = await sendMessage({ threadId, message });
      return response.data;
    },
    onSuccess: (data) => {
      // data.data is now a MessageBatchResult with userMessage and assistantMessage
      const { userMessage } = data.data;

      // Invalidate the messages query to refetch with the new message
      queryClient.invalidateQueries(
        chatMessagesQueryKey(programId, userMessage.threadId)
      );

      // Also invalidate the thread list so the new thread appears in the sidebar
      queryClient.invalidateQueries(['chat-thread-list', programId]);
    },
  });
};

/**
 * Fetch reference card data by type and ID
 *
 * This query retrieves a hydrated reference card (GenericCard, AvatarCard, or ExternalCard)
 * from the chatbot API. The card data includes heading, subheading, action, and type-specific
 * fields like avatars or icons.
 *
 * @param referenceType - Type of reference ('content' | 'user' | 'topic' | 'external')
 * @param referenceId - The unique identifier of the reference
 * @param options - Optional React Query configuration options
 * @returns Query result containing the reference card data
 */
export const useReferenceCardQuery = (
  referenceType: string,
  referenceId: string,
  options?: Omit<
    UseQueryOptions<ReferenceCard, Error, ReferenceCard, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<ReferenceCard, Error, ReferenceCard, string[]>({
    queryKey: referenceCardQueryKey(referenceType, referenceId),
    queryFn: async (): Promise<ReferenceCard> => {
      const response = await getReferenceCard(referenceType, referenceId);
      // Merge attributes with top-level type field
      return {
        ...response.data.attributes,
        type: response.data.type,
      } as ReferenceCard;
    },
    enabled: !!referenceType && !!referenceId,
    ...options,
  });

  return query;
};

/**
 * Fetch conversation starter suggestions
 *
 * This query retrieves random conversation starter suggestions that are shown
 * to users when they first open the chatbot. The suggestions help users get
 * started with their first question.
 *
 * @param programId - The program ID for multi-tenant isolation
 * @param options - Optional React Query configuration options
 * @returns Query result containing the suggestions
 */
export const useSuggestionsQuery = (
  programId: string,
  options?: Omit<
    UseQueryOptions<
      SuggestionsResponse['data'],
      Error,
      SuggestionsResponse['data'],
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<
    SuggestionsResponse['data'],
    Error,
    SuggestionsResponse['data'],
    string[]
  >({
    queryKey: suggestionsQueryKey(programId),
    queryFn: async (): Promise<SuggestionsResponse['data']> => {
      const response = await getSuggestions();
      return response.data.data;
    },
    enabled: !!programId,
    staleTime: 5 * 60 * 1000, // Consider suggestions data fresh for 5 minutes
    ...options,
  });

  return query;
};
